import HttpClient from '@/api/http-client';

export interface CustomerPermissions {
    view: string[];
    action: string[];
}

export interface CustomerSettings {
    permissions: CustomerPermissions;
}

export interface CallcenterItem {
    id: number;
    name: string;
}

export interface CustomerCallcenter {
    queues?: CallcenterItem[] | null;
    agents?: CallcenterItem[] | null;
}

export interface CustomerInfo {
    id: number;
    domain: string;
    callcenter?: CustomerCallcenter;
    settings?: CustomerSettings;
}

export type PermissionIssue = 'no_queues' | 'no_agents';

interface UserInfoApi {
    username: string;
    current_domain: string;
    user_settings: {
        customers: Array<CustomerInfo>;
    };
    users?: {
        [extension: string]: string;
    };
}

export default class UserInfo {
    username: string;

    currentDomain: string;

    customers: Array<CustomerInfo>;

    users: {[extension: string]: string} = {};

    constructor(
        username: string,
        currentDomain: string,
        customers: Array<CustomerInfo>,
        users: {[extension: string]: string} = {},
    ) {
        this.username = username;
        this.currentDomain = currentDomain;
        this.customers = customers;
        this.users = users;
    }

    getUserNameByExtension(extension: string): string | null {
        return this.users[extension] || null;
    }

    getCurrentCustomer(): CustomerInfo | undefined {
        return this.customers.find((c) => c.domain === this.currentDomain);
    }

    hasPermission(permission: string): boolean {
        const customer = this.getCurrentCustomer();
        if (!customer?.settings?.permissions?.view) {
            return false;
        }
        return customer.settings.permissions.view.includes(permission);
    }

    hasActionPermission(permission: string): boolean {
        const customer = this.getCurrentCustomer();
        if (!customer?.settings?.permissions?.action) {
            return false;
        }
        return customer.settings.permissions.action.includes(permission);
    }

    hasAnyActionPermission(): boolean {
        const customer = this.getCurrentCustomer();
        if (!customer?.settings?.permissions?.action) {
            return false;
        }
        return customer.settings.permissions.action.length > 0;
    }

    // espejo de CloudpbxOperator.Policy: sin la clave `*:all` el backend
    // solo deja ver lo que este en callcenter.queues / callcenter.agents
    getPermissionIssues(): PermissionIssue[] {
        const customer = this.getCurrentCustomer();
        if (!customer) {
            return [];
        }

        const view = customer.settings?.permissions?.view || [];
        const queues = customer.callcenter?.queues || [];
        const agents = customer.callcenter?.agents || [];
        const issues: PermissionIssue[] = [];

        if (!view.includes('queues:all') && queues.length === 0) {
            issues.push('no_queues');
        }

        if (!view.includes('agents:all') && agents.length === 0) {
            issues.push('no_agents');
        }

        return issues;
    }

    static fromInit(): UserInfo {
        return new UserInfo('NaN', '', []);
    }

    static async fromApi(http: HttpClient): Promise<UserInfo> {
        const options = {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('token')}`,
            },
        };

        const response = await http.get('/userinfo', {}, options);

        const data = response.data as UserInfoApi;

        return new UserInfo(
            data.username,
            data.current_domain,
            data.user_settings.customers,
            data.users || {},
        );
    }
}
